import axios, { AxiosError, AxiosInstance } from 'axios';
import { getAuth } from './auth';
import { setBearerAuthorization } from './bearerAuthorization';
import { notifySessionExpired } from './sessionExpiry';
import {
  ActionDetail,
  ActionApprovalRequest,
  ActionCommentRequest,
  ActionEvidenceReferenceRequest,
  ActionDirectoryUser,
  ActionGroup,
  ActionGroupPage,
  ActionGroupRevisionConflict,
  ActionPage,
  ActionOutcomeRequest,
  ActionRevisionConflict,
  ActionTransitionRequest,
  ActionSourceResolution,
  EvidenceDownload,
  NotificationPage,
} from '../types/actions';

// A relative base is intentionally same-origin in every environment. Deployment owns the
// reverse-proxy mapping; the browser is never given an action-service host or internal route.
const baseURL = '/api/actions';

const actionClient: AxiosInstance = axios.create({ baseURL, timeout: 30000 });

actionClient.interceptors.request.use(async config => {
  const token = await getAuth().getToken();
  setBearerAuthorization(config.headers, token);
  return config;
});

actionClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const request = error.config as (typeof error.config & { _retry?: boolean });
    if (error.response?.status === 401 && request && !request._retry) {
      try {
        await getAuth().refresh();
        request._retry = true;
        const token = await getAuth().getToken();
        setBearerAuthorization(request.headers, token);
        return actionClient(request);
      } catch (refreshError) {
        notifySessionExpired();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

const attachmentFilename = (header: unknown, fallback: string): string => {
  if (typeof header !== 'string') return fallback;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = header.match(/filename="?([^";]+)"?/i)?.[1];
  try {
    const decoded = decodeURIComponent(encoded || plain || fallback);
    const basename = decoded.split(/[\\/]/).pop() || fallback;
    const forbidden = '<>:"|?*';
    const safe = [...basename]
      .map(character => {
        const code = character.charCodeAt(0);
        return code <= 31 || code === 127 || forbidden.includes(character) ? '_' : character;
      })
      .join('')
      .slice(0, 160);
    return safe.toLowerCase().endsWith('.json') ? safe : `${safe || 'action-evidence'}.json`;
  } catch {
    return fallback;
  }
};

export const actionApi = {
  async list(params: { state?: string; limit?: number; cursor?: string }, signal?: AbortSignal): Promise<ActionPage> {
    const response = await actionClient.get<ActionPage>('/api/v1/actions', { params, signal });
    return response.data;
  },
  async detail(id: string, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.get<ActionDetail>(`/api/v1/actions/${encodeURIComponent(id)}`, { signal });
    return response.data;
  },
  async transition(id: string, request: ActionTransitionRequest, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.post<ActionDetail>(
      `/api/v1/actions/${encodeURIComponent(id)}/transitions`, request, { signal }
    );
    return response.data;
  },
  async approve(id: string, request: ActionApprovalRequest, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.post<ActionDetail>(
      `/api/v1/actions/${encodeURIComponent(id)}/approvals`, request, { signal }
    );
    return response.data;
  },
  async comment(id: string, request: ActionCommentRequest, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.post<ActionDetail>(
      `/api/v1/actions/${encodeURIComponent(id)}/comments`, request, { signal }
    );
    return response.data;
  },
  async addEvidence(id: string, request: ActionEvidenceReferenceRequest, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.post<ActionDetail>(
      `/api/v1/actions/${encodeURIComponent(id)}/evidence-references`, request, { signal }
    );
    return response.data;
  },
  async outcome(id: string, request: ActionOutcomeRequest, signal?: AbortSignal): Promise<ActionDetail> {
    const response = await actionClient.post<ActionDetail>(
      `/api/v1/actions/${encodeURIComponent(id)}/outcomes`, request, { signal }
    );
    return response.data;
  },
  async source(id: string, signal?: AbortSignal): Promise<ActionSourceResolution> {
    const response = await actionClient.get<ActionSourceResolution>(
      `/api/v1/actions/${encodeURIComponent(id)}/source`, { signal }
    );
    return response.data;
  },
  async evidence(id: string, signal?: AbortSignal): Promise<EvidenceDownload> {
    const response = await actionClient.get<Blob>(
      `/api/v1/actions/${encodeURIComponent(id)}/evidence-export`,
      { signal, responseType: 'blob' }
    );
    return {
      blob: response.data,
      filename: attachmentFilename(response.headers['content-disposition'], `action-${id}-evidence.json`),
    };
  },
  async notifications(
    params: { unreadOnly: boolean; limit?: number; cursor?: string },
    signal?: AbortSignal
  ): Promise<NotificationPage> {
    const response = await actionClient.get<NotificationPage>('/api/v1/notifications', { params, signal });
    return response.data;
  },
  async markNotificationRead(id: string, signal?: AbortSignal): Promise<void> {
    await actionClient.put(
      `/api/v1/notifications/${encodeURIComponent(id)}/read`,
      undefined,
      { signal }
    );
  },
  async directoryUsers(query: string, signal?: AbortSignal): Promise<ActionDirectoryUser[]> {
    const response = await actionClient.get<ActionDirectoryUser[]>('/api/v1/action-directory/users', {
      params: { q: query },
      signal,
    });
    return response.data;
  },
  async actionGroups(
    params: { limit?: number; cursor?: string },
    signal?: AbortSignal
  ): Promise<ActionGroupPage> {
    const response = await actionClient.get<ActionGroupPage>('/api/v1/action-groups', {
      params,
      signal,
    });
    return response.data;
  },
  async actionGroup(id: string, signal?: AbortSignal): Promise<ActionGroup> {
    const response = await actionClient.get<ActionGroup>(
      `/api/v1/action-groups/${encodeURIComponent(id)}`,
      { signal }
    );
    return response.data;
  },
  async createActionGroup(request: { name: string; description?: string }): Promise<ActionGroup> {
    const response = await actionClient.post<ActionGroup>('/api/v1/action-groups', request);
    return response.data;
  },
  async updateActionGroup(
    id: string,
    request: { name: string; description?: string; expectedRevision: number }
  ): Promise<ActionGroup> {
    const response = await actionClient.put<ActionGroup>(
      `/api/v1/action-groups/${encodeURIComponent(id)}`,
      request
    );
    return response.data;
  },
  async deactivateActionGroup(id: string, expectedRevision: number): Promise<ActionGroup> {
    const response = await actionClient.post<ActionGroup>(
      `/api/v1/action-groups/${encodeURIComponent(id)}/deactivate`,
      { expectedRevision }
    );
    return response.data;
  },
  async addActionGroupMember(
    id: string,
    request: { principalKey: string; usernameSnapshot: string; expectedRevision: number }
  ): Promise<ActionGroup> {
    const response = await actionClient.post<ActionGroup>(
      `/api/v1/action-groups/${encodeURIComponent(id)}/members`,
      request
    );
    return response.data;
  },
  async removeActionGroupMember(
    id: string,
    request: { principalKey: string; expectedRevision: number }
  ): Promise<ActionGroup> {
    const response = await actionClient.post<ActionGroup>(
      `/api/v1/action-groups/${encodeURIComponent(id)}/members/remove`,
      request
    );
    return response.data;
  },
  isRevisionConflict(error: unknown): error is AxiosError<ActionRevisionConflict> {
    return axios.isAxiosError<ActionRevisionConflict>(error)
      && error.response?.status === 409
      && error.response.data?.error === 'ACTION_REVISION_CONFLICT';
  },
  isActionGroupRevisionConflict(error: unknown): error is AxiosError<ActionGroupRevisionConflict> {
    return axios.isAxiosError<ActionGroupRevisionConflict>(error)
      && error.response?.status === 409
      && error.response.data?.error === 'ACTION_GROUP_REVISION_CONFLICT';
  },
  isNotFound(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 404;
  },
  isCanceled(error: unknown): boolean {
    return axios.isCancel(error);
  },
};

export { actionClient };
